import { useEffect, useRef, type RefObject } from "react";

const FOKUSSIERBAR =
  'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** Offene Dialoge, der oberste zuletzt — nur er reagiert auf Esc und Tab. */
const stapel: object[] = [];

/**
 * Fokusführung für modale Dialoge: setzt den Fokus beim Öffnen hinein, hält
 * Tab im Dialog, schließt mit Esc und gibt den Fokus beim Schließen an das
 * Element zurück, das ihn vorher hatte.
 *
 * `start`: Element, das den Fokus zuerst bekommt. Ohne Angabe das erste
 * fokussierbare Element — es sei denn, ein Kind hat per autoFocus schon
 * selbst den Fokus genommen.
 */
export function useDialogFokus(
  box: RefObject<HTMLElement | null>,
  onEsc: () => void,
  start?: RefObject<HTMLElement | null>,
) {
  const escRef = useRef(onEsc);
  escRef.current = onEsc;

  useEffect(() => {
    const ich = {};
    stapel.push(ich);
    const vorher = document.activeElement as HTMLElement | null;
    const el = box.current;
    if (el && !el.contains(document.activeElement)) {
      const ziel = start?.current ?? el.querySelector<HTMLElement>(FOKUSSIERBAR) ?? el;
      if (ziel === el && !el.hasAttribute("tabindex")) el.setAttribute("tabindex", "-1");
      ziel.focus();
    }

    const taste = (e: KeyboardEvent) => {
      const b = box.current;
      if (!b || stapel[stapel.length - 1] !== ich) return;
      if (e.key === "Escape") {
        // Ein offenes Auswahlfeld oder Menü schließt zuerst sich selbst.
        if (document.querySelector(".ui-select-panel, .kontextmenue")) return;
        // Ebenso eine aufgeklappte Vorschlagsliste im fokussierten Feld.
        if (document.activeElement?.getAttribute("aria-expanded") === "true") return;
        e.preventDefault();
        e.stopPropagation();
        escRef.current();
        return;
      }
      if (e.key !== "Tab") return;
      const liste = [...b.querySelectorAll<HTMLElement>(FOKUSSIERBAR)].filter((x) => x.offsetParent !== null || x === document.activeElement);
      if (liste.length === 0) return;
      const erstes = liste[0]!;
      const letztes = liste[liste.length - 1]!;
      if (!b.contains(document.activeElement)) {
        e.preventDefault();
        erstes.focus();
      } else if (e.shiftKey && document.activeElement === erstes) {
        e.preventDefault();
        letztes.focus();
      } else if (!e.shiftKey && document.activeElement === letztes) {
        e.preventDefault();
        erstes.focus();
      }
    };
    document.addEventListener("keydown", taste, true);
    return () => {
      document.removeEventListener("keydown", taste, true);
      const i = stapel.indexOf(ich);
      if (i >= 0) stapel.splice(i, 1);
      if (vorher && document.contains(vorher)) vorher.focus({ preventScroll: true });
    };
    // Nur beim Öffnen und Schließen — nicht bei jedem Rendern.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

/** Tippt der Nutzer gerade in ein Eingabefeld? Dann gelten keine Tastenkürzel. */
export function istEingabe(ziel: EventTarget | null): boolean {
  const el = ziel as HTMLElement | null;
  if (!el || !el.tagName) return false;
  if (el.isContentEditable) return true;
  if (el.tagName === "TEXTAREA" || el.tagName === "SELECT") return true;
  if (el.tagName === "INPUT") {
    const typ = (el as HTMLInputElement).type;
    return !["checkbox", "radio", "button", "submit", "reset", "range", "color", "file"].includes(typ);
  }
  return false;
}

/**
 * Liegt gerade ein Dialog, Menü oder aufgeklapptes Auswahlfeld über der
 * Oberfläche? Erkennt auch Dialoge anderer Komponenten, solange sie die
 * üblichen Klassen oder aria-modal tragen.
 */
export function ueberlagerungOffen(): boolean {
  return Boolean(
    document.querySelector(
      '[aria-modal="true"], [role="dialog"], [role="alertdialog"], .sheet-backdrop, .kontextmenue, .ui-select-panel',
    ),
  );
}
